import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Shield, FileText, AlertTriangle, Scale, Globe, CreditCard, UserCheck, Mail } from 'lucide-react';

const Terms = () => {
  const lastUpdated = "January 27, 2026";
  
  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight mb-4">
            Terms of Service
          </h1>
          <p className="text-muted-foreground">
            Last updated: {lastUpdated}
          </p>
        </motion.div>

        <Card className="bg-card border-border mb-8">
          <CardContent className="p-6 md:p-8">
            <div className="prose prose-invert max-w-none">
              
              {/* Introduction */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Scale className="w-5 h-5" />
                  1. Introduction
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Welcome to TradersCapital. These Terms of Service ("Terms") govern your use of the TradersCapital 
                  platform and services operated by <strong className="text-foreground">Knowledge Based Trading Ltd</strong> (Company No. 17040814), 
                  registered at 167-169 Great Portland Street, London, England, W1W 5PF,
                  trading as <strong className="text-foreground">Traders Capital</strong> ("Company," "we," "us," or "our").
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  By accessing or using our platform, you agree to be bound by these Terms. If you do not agree 
                  to these Terms, please do not use our services.
                </p>
              </section>

              {/* Nature of Services */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5" />
                  2. Nature of Services
                </h2>
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
                  <p className="text-yellow-400 font-medium mb-2">IMPORTANT DISCLAIMER</p>
                  <p className="text-muted-foreground text-sm">
                    TradersCapital is an <strong className="text-foreground">evaluation-based trading platform</strong>. 
                    We provide simulated trading environments for the purpose of evaluating trading skills and performance.
                  </p>
                </div>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>We are <strong className="text-foreground">NOT</strong> a financial advisory service and do not provide financial advice.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>We are <strong className="text-foreground">NOT</strong> a fund management company and do not manage client funds.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>We are <strong className="text-foreground">NOT</strong> a broker and do not execute real market trades.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Our platform provides <strong className="text-foreground">simulated trading environments</strong> for skill evaluation purposes.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Successful completion of our evaluation does not guarantee future trading success or profits.</span>
                  </li>
                </ul>
              </section>

              {/* Eligibility */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <UserCheck className="w-5 h-5" />
                  3. Eligibility
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  To use our services, you must:
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Be at least 18 years of age (or the age of majority in your jurisdiction)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Have the legal capacity to enter into binding contracts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Not be prohibited from using our services under applicable laws</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Provide accurate and complete information during registration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Complete KYC (Know Your Customer) verification for funded account payouts</span>
                  </li>
                </ul>
              </section>

              {/* Evaluation Process */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Shield className="w-5 h-5" />
                  4. Evaluation Process
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Our evaluation process consists of:
                </p>
                <div className="space-y-4">
                  <div className="p-4 bg-secondary/30 rounded-lg">
                    <h4 className="font-bold text-foreground mb-2">Phase 1 Evaluation</h4>
                    <p className="text-muted-foreground text-sm">
                      Achieve an 8% profit target while adhering to maximum drawdown limits. 
                      No time limit applies.
                    </p>
                  </div>
                  <div className="p-4 bg-secondary/30 rounded-lg">
                    <h4 className="font-bold text-foreground mb-2">Phase 2 Evaluation</h4>
                    <p className="text-muted-foreground text-sm">
                      Achieve a 5% profit target while maintaining risk management rules. 
                      No time limit applies.
                    </p>
                  </div>
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <h4 className="font-bold text-green-400 mb-2">Funded Account</h4>
                    <p className="text-muted-foreground text-sm">
                      Upon successful completion, you receive access to a funded trading account 
                      with profit-sharing arrangements as specified in your chosen plan.
                    </p>
                  </div>
                </div>
              </section>

              {/* Payments & Refunds */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5" />
                  5. Payments & Refunds
                </h2>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>All challenge fees are one-time payments and are <strong className="text-foreground">non-refundable</strong> once the evaluation has commenced.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>First-time buyers receive a <strong className="text-foreground">free retake</strong> if they fail their first challenge.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Payouts from funded accounts are subject to KYC verification and our payout schedule.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Standard payout eligibility: After a minimum of 6 trading days, then every 14 days thereafter.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Payout on Demand upgrade: Withdraw anytime with no waiting periods.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>All payouts are processed within 24 hours of approval.</span>
                  </li>
                </ul>
              </section>

              {/* Prohibited Activities */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5" />
                  6. Prohibited Activities
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  The following activities are strictly prohibited and may result in account termination:
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-destructive mt-1">✗</span>
                    <span>Using third-party trading software, bots, or automated systems without approval</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-destructive mt-1">✗</span>
                    <span>Exploiting platform bugs, glitches, or technical vulnerabilities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-destructive mt-1">✗</span>
                    <span>Account sharing or allowing others to trade on your behalf</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-destructive mt-1">✗</span>
                    <span>Providing false or misleading information during registration or KYC</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-destructive mt-1">✗</span>
                    <span>Engaging in any form of market manipulation or abusive trading practices</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-destructive mt-1">✗</span>
                    <span>Using the platform from prohibited jurisdictions</span>
                  </li>
                </ul>
              </section>

              {/* Risk Disclosure */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5" />
                  7. Risk Disclosure
                </h2>
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-4">
                  <p className="text-destructive font-medium mb-2">HIGH RISK WARNING</p>
                  <p className="text-muted-foreground text-sm">
                    Trading financial instruments involves substantial risk of loss and is not suitable for all investors. 
                    Past performance is not indicative of future results.
                  </p>
                </div>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>You should only trade with money you can afford to lose.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Success in our evaluation does not guarantee future trading profits.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>We recommend seeking independent financial advice before trading.</span>
                  </li>
                </ul>
              </section>

              {/* Intellectual Property */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Globe className="w-5 h-5" />
                  8. Intellectual Property
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  All content, trademarks, logos, and intellectual property on this platform are owned by 
                  Knowledge Based Trading Ltd or its licensors. You may not reproduce, distribute, or create 
                  derivative works without our express written permission.
                </p>
              </section>

              {/* Limitation of Liability */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Shield className="w-5 h-5" />
                  9. Limitation of Liability
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  To the maximum extent permitted by law:
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>We are not liable for any indirect, incidental, or consequential damages.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Our total liability shall not exceed the fees paid by you in the 12 months preceding the claim.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>We are not responsible for losses due to technical failures, market conditions, or third-party actions.</span>
                  </li>
                </ul>
              </section>

              {/* Governing Law */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Scale className="w-5 h-5" />
                  10. Governing Law
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of England and Wales.
                  Knowledge Based Trading Ltd (Company No. 17040814) is registered in England.
                  Any disputes shall be resolved through binding arbitration or in the courts of England and Wales.
                </p>
              </section>

              {/* Changes to Terms */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5" />
                  11. Changes to Terms
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  We reserve the right to modify these Terms at any time. Changes will be effective upon posting 
                  to our platform. Your continued use of our services after changes constitutes acceptance of 
                  the modified Terms.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Mail className="w-5 h-5" />
                  12. Contact Information
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  For questions about these Terms, please contact us:
                </p>
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-foreground font-medium">Knowledge Based Trading Ltd</p>
                  <p className="text-muted-foreground text-sm">Trading as: Traders Capital</p>
                  <p className="text-muted-foreground text-sm">Company No: 17040814</p>
                  <p className="text-muted-foreground text-sm mt-2">Registered Office: 167-169 Great Portland Street, London, England, W1W 5PF</p>
                  <p className="text-muted-foreground text-sm mt-2">Email: support@traderscapital.net</p>
                </div>
              </section>

            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Terms;
